import { inject, Injectable } from "@angular/core";
import { STORE_NAME_PROJECT_FLOORPLANS } from "@app/indexed-db-config";
import { ProjectApiService } from "@domain/project/api/project-api.service";
import { Floorplan } from "@domain/project/floorplan/floorplan";
import { FloorplanEvent, FloorplanEventType } from "@domain/project/floorplan/floorplan-event";
import { ProjectEventType } from "@domain/project/project-event";
import type { ScopedProject } from "@domain/project/project-guards";
import { SyncState } from "@domain/project/project-image.service";
import { ErrorReportingService } from "@utils/error-reporting.service";
import { instanceToPlain, plainToInstance } from "class-transformer";
import { NgxIndexedDBService } from "ngx-indexed-db";
import {
  BehaviorSubject,
  catchError,
  concatMap,
  debounceTime,
  EMPTY,
  forkJoin,
  map,
  Observable,
  of,
  retry,
  Subject,
  switchMap,
  tap,
} from "rxjs";
import { v4 as uuidv4 } from "uuid";

export interface ProjectFloorplanData {
  floorplan: Floorplan;
  projectId: string;
  syncState: SyncState;
}

@Injectable({
  providedIn: "root",
})
export class ProjectFloorplanService {
  private dbService = inject(NgxIndexedDBService);
  private projectApiService = inject(ProjectApiService);
  private errorReportingService = inject(ErrorReportingService);

  private readonly _floorplans = new BehaviorSubject<Floorplan[]>([]);
  private readonly _floorplanEvents = new Subject<FloorplanEvent>();

  constructor() {
    this._floorplanEvents
      .pipe(
        debounceTime(0),
        concatMap((event) =>
          this.updateFloorplanInIndexedDB(event.floorplan).pipe(
            retry({ count: 2, delay: 500 }),
            // This service is provided in root: an error reaching the outer subscriber would disable
            // floorplan persistence for every project until the page is reloaded.
            catchError((error) => {
              console.error("Failed to persist floorplan change", error);
              this.errorReportingService.captureException(error, {
                tags: { area: "floorplan-autosave" },
                extra: { floorplanId: event.floorplan?.id, projectId: event.project?.id, eventType: event.type },
              });
              return EMPTY;
            }),
          ),
        ),
      )
      .subscribe();
  }

  get floorplans$(): Observable<Floorplan[]> {
    return this._floorplans.asObservable();
  }

  get floorplans(): Floorplan[] {
    return this._floorplans.getValue();
  }

  get floorplanEvents$(): Observable<FloorplanEvent> {
    return this._floorplanEvents.asObservable();
  }

  loadFloorplans(project: ScopedProject): Observable<ProjectFloorplanData[]> {
    return this.getFloorplansFromIndexedDB(project.id).pipe(
      map((items) =>
        items
          .filter((item) => item.syncState !== SyncState.DELETED)
          .map((item) => {
            item.floorplan.init(project, (f, type, subject) => this.publishFloorplanEvent(f, type, subject));
            return item;
          }),
      ),
      tap((items) => {
        const floorplans = items.map((item) => item.floorplan);
        this._floorplans.next(floorplans);
        project.cachedFloorplans = floorplans;
      }),
    );
  }

  saveFloorplanToIndexedDB(project: ScopedProject, name: string, fileUrl?: string, description?: string, tags?: string[]) {
    const floorplan = Floorplan.create(project as unknown as ScopedProject, name, fileUrl, description, tags);
    floorplan.init(project as unknown as ScopedProject);
    if (!floorplan) {
      throw new Error("Failed to create floorplan");
    }
    const dataToStore = {
      id: floorplan.id,
      floorplan: instanceToPlain(floorplan),
      projectId: project.id,
      syncState: SyncState.NEW,
    };
    return this.dbService.add(STORE_NAME_PROJECT_FLOORPLANS, dataToStore).pipe(
      tap(() => this._floorplans.next([...this._floorplans.getValue(), floorplan])),
      tap(() => project.publishUpdate(ProjectEventType.FLOORPLAN_ADDED, floorplan)),
    );
  }

  getFloorplansFromIndexedDB(projectId: string): Observable<ProjectFloorplanData[]> {
    return this.dbService.getAll<any>(STORE_NAME_PROJECT_FLOORPLANS).pipe(
      map((items) => items.filter((item) => item.projectId === projectId)),
      map((items) =>
        items.map((item) => ({
          floorplan: plainToInstance(Floorplan, item.floorplan as unknown as Floorplan),
          projectId: item.projectId,
          syncState: item.syncState,
        })),
      ),
    );
  }

  removeFloorplan(floorplan: Floorplan, project: ScopedProject): Observable<void> {
    return this.dbService.getByKey<any>(STORE_NAME_PROJECT_FLOORPLANS, floorplan.id).pipe(
      switchMap((entry) => {
        if (!entry) {
          console.error(`No floorplan found for floorplanId=${floorplan.id}`);
          return of(void 0);
        }
        const updated = { ...entry, syncState: SyncState.DELETED };
        return this.dbService.update(STORE_NAME_PROJECT_FLOORPLANS, updated).pipe(
          tap(() => {
            const remaining = this._floorplans.getValue().filter((fp) => fp.id !== floorplan.id);
            this._floorplans.next(remaining);
            project.cachedFloorplans = remaining;
          }),
          tap(() => project.setAllPositionIds()),
          switchMap(() => this.persistRemainingFloorplans()),
          tap(() => project.publishUpdate(ProjectEventType.FLOORPLAN_DELETED, floorplan)),
          map(() => void 0),
        );
      }),
    );
  }

  private persistRemainingFloorplans(): Observable<void> {
    const remaining = this._floorplans.getValue();
    if (remaining.length === 0) {
      return of(void 0);
    }
    return forkJoin(remaining.map((fp) => this.updateFloorplanInIndexedDB(fp))).pipe(map(() => void 0));
  }

  updateFloorplanInIndexedDB(floorplan: Floorplan) {
    return this.dbService.getByKey<any>(STORE_NAME_PROJECT_FLOORPLANS, floorplan.id).pipe(
      switchMap((entry) => {
        if (!entry) {
          console.error(`No floorplan found for floorplanId=${floorplan.id} while trying to update`);
          return of(void 0);
        }
        const syncState = entry.syncState === SyncState.NEW ? SyncState.NEW : SyncState.UPDATED;
        const updated = { ...entry, floorplan: instanceToPlain(floorplan), syncState };
        return this.dbService.update(STORE_NAME_PROJECT_FLOORPLANS, updated).pipe(map(() => void 0));
      }),
    );
  }

  removeFloorplanFromIndexedDB(floorplanId: string): Observable<void> {
    if (!floorplanId) {
      console.error(`No floorplan found for floorplanId=${floorplanId} while trying to remove`);
      return of(void 0);
    }
    return this.dbService.deleteByKey(STORE_NAME_PROJECT_FLOORPLANS, floorplanId);
  }

  deleteProjectFloorplansFromIndexedDB(projectId: string) {
    return this.dbService.getAll<any>(STORE_NAME_PROJECT_FLOORPLANS).pipe(
      map((items) => items.filter((item) => item.projectId === projectId)),
      switchMap((items) => {
        if (items.length === 0) {
          return of(void 0);
        }
        const deleteObservables = items.map((item) => this.dbService.deleteByKey(STORE_NAME_PROJECT_FLOORPLANS, item.id));
        return forkJoin(deleteObservables).pipe(map(() => void 0));
      }),
    );
  }

  getFloorplanFromIndexedDB(floorplanId: string): Observable<Floorplan> {
    return this.dbService.getByKey<any>(STORE_NAME_PROJECT_FLOORPLANS, floorplanId).pipe(
      map((item) => {
        if (!item) {
          throw new Error("Floorplan not found");
        }
        return plainToInstance(Floorplan, item.floorplan as unknown as Floorplan);
      }),
    );
  }

  publishFloorplanEvent(floorplan: Floorplan, type: FloorplanEventType, subject: any) {
    floorplan.project.publishUpdate(ProjectEventType.FLOORPLAN_UPDATED, subject);
    this._floorplanEvents.next({ project: floorplan.project, floorplan, type, subject });
  }

  saveFloorplansToIndexedDB(floorplanData: ProjectFloorplanData[]): Observable<void> {
    const dataToStore = floorplanData.map((data) => {
      if (!data.floorplan || !data.projectId) {
        throw new Error("Missing required floorplan data");
      }
      if (!data.floorplan.id) {
        throw new Error(`Floorplan is missing an id (projectId=${data.projectId})`);
      }
      const id = data.floorplan.id;
      return {
        id,
        floorplan: instanceToPlain(data.floorplan),
        projectId: data.projectId,
        syncState: data.syncState,
      };
    });
    return this.dbService.bulkPut(STORE_NAME_PROJECT_FLOORPLANS, dataToStore).pipe(map(() => void 0));
  }

  getUnsyncedFloorplans(projectId: string) {
    return this.dbService.getAll<any>(STORE_NAME_PROJECT_FLOORPLANS).pipe(
      map((items) => items.filter((item) => item.syncState && item.syncState !== SyncState.SYNCED)),
      map((items) => items.filter((item) => item.projectId === projectId)),
      map((items) =>
        items.map((item) => ({
          floorplan: plainToInstance(Floorplan, item.floorplan as unknown as Floorplan),
          projectId: item.projectId,
          syncState: item.syncState,
        })),
      ),
    );
  }

  /** Emits true only when the API confirmed every floorplan. Successful entries are marked as synced either way. */
  addFloorplans(floorplans: ProjectFloorplanData[], cloudId: string): Observable<boolean> {
    if (!cloudId) {
      throw new Error("cloudId is undefined");
    }

    const floorplansToSync = floorplans.map((f) => f.floorplan);
    return this.projectApiService.addFloorplans(cloudId, floorplansToSync).pipe(
      switchMap((response: any[]) => {
        const entries = response ?? [];
        const allSucceeded = entries.length === floorplansToSync.length && entries.every((res) => res?.success);
        const syncCalls = entries.filter((res) => res?.success).map((res) => this.markFloorplanAsSynced(res.floorplanId));

        if (syncCalls.length === 0) {
          return of(allSucceeded);
        }

        return forkJoin(syncCalls).pipe(map(() => allSucceeded));
      }),
    );
  }

  /** Emits true only when the API confirmed every deletion. */
  removeFloorplans(floorplans: ProjectFloorplanData[], cloudId: string): Observable<boolean> {
    const ids = floorplans.map((floorplan) => floorplan.floorplan.id).filter((id): id is string => !!id);

    if (ids.length === 0) {
      return of(true);
    }

    return this.projectApiService.deleteFloorplans(cloudId, ids).pipe(
      switchMap((response: any[]) => {
        const entries = response ?? [];
        const allSucceeded = entries.length === ids.length && entries.every((res) => res?.success);
        const syncCalls = entries.map((res) => this.removeFloorplanFromIndexedDB(res.floorplanId));

        if (syncCalls.length === 0) {
          return of(allSucceeded);
        }

        return forkJoin(syncCalls).pipe(map(() => allSucceeded));
      }),
    );
  }

  markFloorplanAsSynced(floorplanId: string): Observable<void> {
    return this.dbService.getByKey<any>(STORE_NAME_PROJECT_FLOORPLANS, floorplanId).pipe(
      switchMap((entry) => {
        if (!entry) {
          console.error(`No floorplan found for floorplanId=${floorplanId}`);
          return of(void 0);
        }
        const updated = { ...entry, syncState: SyncState.SYNCED };
        return this.dbService.update(STORE_NAME_PROJECT_FLOORPLANS, updated).pipe(map(() => void 0));
      }),
    );
  }

  /**
   * Loads the cloud floorplans of the project and stores them as SYNCED. Emits the ids of the cloud floorplans.
   *
   * With `overwriteLocalChanges = false` (normal project open) every cloud floorplan whose local record is not
   * SYNCED is skipped, so local work that has not reached the cloud yet survives.
   * With `overwriteLocalChanges = true` (the user discarded the local copy) those records are exactly the ones
   * that have to be replaced, so the guard is deliberately bypassed.
   */
  private fetchAndStoreCloudFloorplans(project: ScopedProject, overwriteLocalChanges: boolean): Observable<Set<string>> {
    return forkJoin([
      this.projectApiService.loadProjectFloorplans(project.cloudId!),
      this.getFloorplansFromIndexedDB(project.id),
    ]).pipe(
      switchMap(([cloudFloorplans, localFloorplans]) => {
        const cloudIds = new Set<string>(cloudFloorplans.map((apiFloorplan) => apiFloorplan.id));
        const protectedIds = overwriteLocalChanges
          ? new Set<string>()
          : new Set<string>(
              localFloorplans.filter((entry) => entry.syncState !== SyncState.SYNCED).map((entry) => entry.floorplan.id),
            );

        const dataToStore = cloudFloorplans
          .filter((apiFloorplan) => !protectedIds.has(apiFloorplan.id))
          .map((apiFloorplan) => ({
            id: apiFloorplan.id,
            floorplan: this.normalizeFloorplanKeys(apiFloorplan),
            projectId: project.id,
            syncState: SyncState.SYNCED,
          }));

        if (dataToStore.length === 0) {
          return of(cloudIds);
        }

        return this.dbService.bulkPut(STORE_NAME_PROJECT_FLOORPLANS, dataToStore).pipe(map(() => cloudIds));
      }),
    );
  }

  getProjectFloorplans(project: ScopedProject): Observable<void> {
    return this.fetchAndStoreCloudFloorplans(project, false).pipe(map(() => void 0));
  }

  /**
   * Resets every floorplan record of the project to the cloud state: cloud floorplans are stored as SYNCED,
   * local-only floorplans are hard-deleted. Used when the user discards the local copy in a version conflict -
   * without the delete, the local-only floorplans would be uploaded into the adopted cloud project by the
   * next sync cycle.
   */
  replaceFloorplansWithCloudVersion(project: ScopedProject): Observable<void> {
    return this.fetchAndStoreCloudFloorplans(project, true).pipe(
      switchMap((cloudIds) => this.deleteFloorplansNotInCloud(project.id, cloudIds)),
    );
  }

  /**
   * Prepares the floorplan records of the project so that the local state wins over the cloud state:
   * every already SYNCED record is flagged as UPDATED and every cloud floorplan without a local counterpart
   * is written as a DELETED tombstone. Used when the user overwrites the cloud version in a version conflict.
   *
   * The flags do both halves of the job: they protect the local floorplans from being pulled over by the
   * cloud versions on the next project open (see the dirty guard in fetchAndStoreCloudFloorplans) and they
   * queue the records for upload, so the cloud ends up with exactly the local floorplans.
   */
  stageLocalFloorplansForCloudOverwrite(project: ScopedProject): Observable<void> {
    return forkJoin([
      this.projectApiService.loadProjectFloorplans(project.cloudId!),
      this.dbService.getAll<any>(STORE_NAME_PROJECT_FLOORPLANS),
    ]).pipe(
      switchMap(([cloudFloorplans, storedItems]) => {
        const localItems = storedItems.filter((item) => item.projectId === project.id);
        const localIds = new Set<string>(localItems.map((item) => item.id));

        // Records that already carry local changes (NEW / UPDATED / DELETED) are left untouched - they are
        // dirty already and are uploaded as they are.
        const floorplansToReupload = localItems
          .filter((item) => item.syncState === SyncState.SYNCED)
          .map((item) => ({ ...item, syncState: SyncState.UPDATED }));

        // A floorplan that only exists in the cloud is part of the version the user decided to discard.
        // The tombstone hides it locally and makes the next sync remove it from the cloud.
        const cloudOnlyTombstones = cloudFloorplans
          .filter((apiFloorplan) => !localIds.has(apiFloorplan.id))
          .map((apiFloorplan) => ({
            id: apiFloorplan.id,
            floorplan: this.normalizeFloorplanKeys(apiFloorplan),
            projectId: project.id,
            syncState: SyncState.DELETED,
          }));

        const dataToStore = [...floorplansToReupload, ...cloudOnlyTombstones];
        if (dataToStore.length === 0) {
          return of(void 0);
        }

        return this.dbService.bulkPut(STORE_NAME_PROJECT_FLOORPLANS, dataToStore).pipe(map(() => void 0));
      }),
    );
  }

  private deleteFloorplansNotInCloud(projectId: string, cloudIds: ReadonlySet<string>): Observable<void> {
    return this.dbService.getAll<any>(STORE_NAME_PROJECT_FLOORPLANS).pipe(
      map((items) => items.filter((item) => item.projectId === projectId && !cloudIds.has(item.id))),
      switchMap((items) => {
        if (items.length === 0) {
          return of(void 0);
        }
        const deleteObservables = items.map((item) => this.dbService.deleteByKey(STORE_NAME_PROJECT_FLOORPLANS, item.id));
        return forkJoin(deleteObservables).pipe(map(() => void 0));
      }),
    );
  }

  /**
   * Copies all floorplans of `oldProjectId` into `newProject`, assigning each copied floorplan a new id.
   * Returns a map of old floorplan id -> new floorplan id, so that callers can remap other project data
   * (e.g. product item groups / product item references) that refer to the original floorplan ids.
   */
  copyFloorplans(
    oldProjectId: string,
    newProject: ScopedProject,
    imageIdMap: ReadonlyMap<string, string>,
  ): Observable<Map<string, string>> {
    return this.getFloorplansFromIndexedDB(oldProjectId).pipe(
      switchMap((floorplanData) => {
        const floorplanIdMap = new Map<string, string>();
        const copiedData = floorplanData
          .filter((floorplan) => floorplan.syncState !== SyncState.DELETED)
          .map((floorplan) => {
            const newId = uuidv4();
            floorplanIdMap.set(floorplan.floorplan.id, newId);
            const plainFloorplan = {
              ...instanceToPlain(floorplan.floorplan),
              id: newId,
            };
            this.remapProjectImageIds(plainFloorplan, imageIdMap);
            return {
              id: newId,
              floorplan: plainFloorplan,
              projectId: newProject.id,
              syncState: SyncState.NEW,
            };
          });

        if (copiedData.length === 0) {
          return of(floorplanIdMap);
        }

        return this.dbService.bulkPut(STORE_NAME_PROJECT_FLOORPLANS, copiedData).pipe(map(() => floorplanIdMap));
      }),
    );
  }

  /** Replaces references to copied project images (`projectImageId`) inside a plain floorplan with the new image ids,
   * since copyImages() assigns new ids to the copied images. */
  private remapProjectImageIds(plainFloorplan: any, imageIdMap: ReadonlyMap<string, string>): void {
    if (imageIdMap.size === 0 || !Array.isArray(plainFloorplan?.images)) {
      return;
    }
    plainFloorplan.images = plainFloorplan.images.map((image: any) => ({
      ...image,
      projectImageId: imageIdMap.get(image.projectImageId) ?? image.projectImageId,
    }));
  }

  /**
   * Maps floorplanState keys from the API format (e.g. "exZonesVisible") to the
   * @Expose({ name: "..." }) short names used by class-transformer (e.g. "exZones").
   * Other keys that already match the Expose names or have no mapping are left unchanged.
   */
  private readonly floorplanStateKeyMap: Record<string, string> = {
    exZonesVisible: "exZones",
    dustExZonesVisible: "dustExZones",
    wildcardZonesVisible: "wildcardZones",
    monitoringAreasVisible: "monitoringAreas",
    dangerAreasVisible: "dangerAreas",
    imagesVisible: "images",
    gasWarningCentersVisible: "gasWarningCenters",
    transmittersVisible: "transmitters",
    alarmDevicesVisible: "alarmDevices",
    signalElementsVisible: "signalElements",
    plasticSignsVisible: "plasticSigns",
    floorplanTextsVisible: "floorplanTexts",
    measurementLinesVisible: "measurementLines",
    airPathsVisible: "airPaths",
    pipelinesVisible: "pipelines",
    cableRoutesVisible: "cableRoutes",
    protectiveTubesVisible: "protectiveTubes",
    wiresVisible: "wires",
    patientMonitorsVisible: "patientMonitors",
    isolationRoomsVisible: "isolationRooms",
    itRoomsVisible: "itRooms",
    operatingTheatresVisible: "operatingTheatres",
    procedureRoomsVisible: "procedureRooms",
    standardRoomsVisible: "standardRooms",
  };

  private normalizeFloorplanKeys(obj: any, isFloorplanState = false): any {
    if (Array.isArray(obj)) {
      return obj.map((item) => this.normalizeFloorplanKeys(item));
    }
    if (obj !== null && typeof obj === "object") {
      return Object.fromEntries(
        Object.entries(obj).map(([key, value]) => {
          let normalizedKey = key.startsWith("_") ? key.slice(1) : key;
          if (isFloorplanState && this.floorplanStateKeyMap[normalizedKey]) {
            normalizedKey = this.floorplanStateKeyMap[normalizedKey];
          }
          const isNestedFloorplanState = normalizedKey === "floorplanState";
          return [normalizedKey, this.normalizeFloorplanKeys(value, isNestedFloorplanState)];
        }),
      );
    }
    return obj;
  }
}
